import { type ColumnDef } from "@tanstack/react-table";
import type { ApiDocumentation } from "../types";
import type { TFunction } from "i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CircleEllipsis,
  Download,
  ExternalLink,
  FileText,
  Link as LinkIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

function getFileName(url: string): string {
  try {
    return new URL(url).pathname.split("/").filter(Boolean).at(-1) ?? url;
  } catch {
    return url;
  }
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  }
  return `${Math.round(bytes / 1024)} Ko`;
}

function formatUrlDisplay(url: string): string {
  try {
    const { hostname, pathname, search } = new URL(url);
    const host = hostname.replace(/^www\./, "");
    const path = `${pathname}${search}`.replace(/\/$/, "");
    return path && path !== "/" ? `${host}${path}` : host;
  } catch {
    return url.replace(/^https?:\/\//, "").replace(/^www\./, "");
  }
}

export const docsColumns = (t: TFunction): ColumnDef<ApiDocumentation>[] => [
  {
    id: "reference",
    header: t("modules.docs.keys.reference"),
    cell: ({ row }) => {
      const dilemmas = row.original.dilemmas;
      const first = dilemmas[0];
      const extra = dilemmas.length - 1;
      return (
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-sm">{first?.reference ?? "—"}</span>
          {extra > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
              +{extra}
            </Badge>
          )}
        </div>
      );
    },
  },
  {
    id: "dilemma_name",
    header: t("modules.docs.keys.dilemma"),
    cell: ({ row }) => (
      <span className="truncate inline-block max-w-64 text-sm">
        {row.original.dilemmas[0]?.name ?? "—"}
      </span>
    ),
  },
  {
    id: "resource",
    header: t("modules.docs.keys.resource"),
    cell: ({ row }) => {
      const item = row.original;
      if (item.kind === "uploaded_file") {
        return (
          <a
            href={item.uploaded_file}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex max-w-72 items-center gap-2 text-sm text-foreground/80 transition-colors hover:text-primary"
          >
            <FileText className="h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-primary" />
            <span className="truncate font-mono group-hover:underline">
              {getFileName(item.uploaded_file)}
            </span>
            <ExternalLink className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
          </a>
        );
      }
      return (
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          title={item.title ? item.link : undefined}
          className="group inline-flex max-w-72 items-center gap-2 text-sm text-foreground/80 transition-colors hover:text-primary"
        >
          <LinkIcon className="h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-primary" />
          <span className="truncate group-hover:underline">
            {item.title || formatUrlDisplay(item.link)}
          </span>
          <ExternalLink className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
        </a>
      );
    },
  },
  {
    id: "file_size",
    header: t("modules.docs.keys.file_size"),
    cell: ({ row }) => {
      const item = row.original;
      return (
        <span className="text-sm text-gray-500">
          {item.kind === "uploaded_file" ? formatFileSize(item.file_size) : "—"}
        </span>
      );
    },
  },
  {
    id: "actions",
    header: t("keys.table.actions"),
    cell: ({ row }) => {
      const item = row.original;
      const url =
        item.kind === "uploaded_file" ? item.uploaded_file : item.link;
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <span className="sr-only">Ouvrir le menu</span>
              <CircleEllipsis className="h-10 w-10 rotate-90 text-gray-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {item.kind === "uploaded_file" && (
              <DropdownMenuItem asChild>
                <a href={url} download={getFileName(url)}>
                  <Download />
                  {t("modules.docs.actions.download")}
                </a>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
            >
              <ExternalLink />
              {t("modules.docs.actions.open")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
